import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardActionArea from '@mui/material/CardActionArea'
import CardContent from '@mui/material/CardContent'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'

import Button from '@/shared/Button'
import { useEffect, useState } from 'react'

import ConfirmDialog from '@/shared/ConfirmDialog'
import { ContentCopy } from '@mui/icons-material'
import AddCircle from '@mui/icons-material/AddCircle'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import CreateDomainDialog from '@/components/CreateDomainDialog'
import { useNavigate } from 'react-router-dom'
import PermissionsController from '@/components/PermissionsController'

const mapStateToProps = ({ company }) => ({ dnsRecord: company.dnsRecord })

const VerifyDomainPage = ({ dispatch, dnsRecord }) => {
  const [showTemplate, setShowTemplate] = useState(false)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const { t } = useTranslation()
  const navigate = useNavigate()

  useEffect(() => {
    dispatch({ type: 'company/getDNS' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Container maxWidth={false}>
      <Grid container spacing={2} my={1}>
        <PermissionsController permission="verifiedDomain_create">
          <Grid item mr={2}>
            <Button
              variant="text"
              color="secondary_80"
              endIcon={<AddCircle sx={{ color: '#2D2D6C80' }} />}
              onClick={() => {
                setShowCreateDialog(true)
              }}
            >
              {t('general.add_domain')}
            </Button>
          </Grid>
        </PermissionsController>
      </Grid>

      <Stack spacing={4}>
        <Collapsible title="為什麼需要域名驗證">
          <Typography>
            驗證您的域名表明您在組織內擁有足夠的權限，可以發送釣魚攻擊模擬和培訓郵件。這一步防止了惡意行為者冒充您組織的成員。
          </Typography>
        </Collapsible>
        <Collapsible title="如何驗證您的域名">
          <Typography>
            透過您的域名主機（通常是您購買域名的地方）進行域名驗證。有關詳細信息，請參閱辨識您的域名主機。您需要具備修改組織的
            DNS 記錄的能力，或聯繫能夠執行此項任務的指定團隊。 Phish Insight 提供了一個 TXT
            驗證記錄，您需要將其添加到您域名主機的 DNS 記錄中。當 Phish Insight
            檢測到該記錄存在時，將確認您對該域名的所有權。驗證記錄不會影響您的網站或電子郵件。
          </Typography>
        </Collapsible>
        <Collapsible title="新增域名驗證">
          <Stack spacing={2}>
            <Stack direction={'row'} spacing={2} alignItems={'center'}>
              <Typography>TXT 金鑰</Typography>
              <Stack
                alignItems={'center'}
                direction={'row'}
                spacing={1}
                sx={{
                  border: '1px solid black',
                  borderRadius: 1,
                  py: 0.5,
                  px: 1,
                  background: 'white',
                }}
              >
                <Typography>{dnsRecord}</Typography>
                <IconButton
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText('93AM3RV3DKL4HQPOL1SGV1TM58ZKGCZ590SSFNO5')

                    dispatch({
                      type: 'app/notify',
                      payload: { message: t('messages.clipboard_copied'), severity: 'success' },
                    })
                  }}
                >
                  <ContentCopy />
                </IconButton>
              </Stack>
            </Stack>
            <Typography>
              請等待您的 DNS 配置更改生效，這可能需要最多72小時，具體取決於您的域名主機。
            </Typography>
            <Typography component={'div'}>
              完成 DNS 配置後，返回此處，然後在列表上點擊“驗證”。
              如果您在此過程中遇到困難，請向您的IT部門尋求幫助。這是聯繫IT的
              <Typography
                color={theme => theme.palette.secondary_120.main}
                display={'inline'}
                sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                onClick={() => {
                  setShowTemplate(true)
                }}
              >
                範例電子郵件
              </Typography>
              。
              <Typography>請勿隨時從您的伺服器中刪除該記錄，因為我們需要定期進行驗證。</Typography>
            </Typography>
          </Stack>
        </Collapsible>
        <Collapsible title="確認驗證的域名">
          <Typography component="div">
            羽量級（未驗證）用戶可以發送到1個域名。欲瞭解更多有關我們的服務計劃的信息，請參閱
            <Typography
              color={theme => theme.palette.secondary_120.main}
              display={'inline'}
              sx={{ textDecoration: 'underline', cursor: 'pointer' }}
              onClick={() => {
                navigate('/personal_details')
              }}
            >
              資訊
            </Typography>
            說明。
          </Typography>
        </Collapsible>
      </Stack>

      <ConfirmDialog
        open={showTemplate}
        actionsSx={{ display: 'none' }}
        dialogSx={{
          '& .MuiDialog-paper': {},
        }}
        onClose={() => {
          setShowTemplate(false)
        }}
      >
        <Typography fontWeight={'bold'} color={theme => theme.palette.secondary_80.main}>
          範例電子郵件模板
        </Typography>
        <Typography fontWeight={'bold'} color={theme => theme.palette.secondary_80.main}>
          您可以按照下面的範例電子郵件範本請求您的 IT 人員或管理您的網域的團隊為您新增 TXT 記錄。
        </Typography>
        <Box sx={{ border: '1px solid black', p: 2, mt: 2 }}>
          <Typography>
            您好，我們即將向選定的員工發起網路釣魚模擬/培訓活動。我們將使用台哥大的社交工程服務來發起此活動。作為其安全程序的一部分，需要驗證我們的域名，以證明我們擁有發送活動電子郵件的足夠權限。此驗證要求我們將
            TXT 金鑰新增至我們的 DNS
            記錄。我們希望尋求您的幫助來執行此操作。所有資訊都可以在域名驗證的頁面中中找到。預先感謝您。
          </Typography>
          <Typography>此致，</Typography>
          <Typography>{`{{ 名稱 }},`}</Typography>
          <Typography>{`{{ 部門 }}`}</Typography>
        </Box>
      </ConfirmDialog>

      <CreateDomainDialog
        open={showCreateDialog}
        onClose={() => {
          setShowCreateDialog(false)
        }}
      />
    </Container>
  )
}

const Collapsible = ({ title, children }) => {
  const [expanded, setExpanded] = useState(false)

  return (
    <Card sx={{ background: '#2D2D6C1A' }} elevation={0}>
      <CardActionArea>
        <CardContent
          onClick={() => {
            setExpanded(!expanded)
          }}
        >
          <Stack spacing={2}>
            <Stack direction={'row'} spacing={2}>
              <Box>
                <Typography color={theme => theme.palette.secondary_80.main} fontWeight={'bold'}>
                  {title}
                </Typography>
              </Box>
              {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>

      {expanded && (
        <CardContent>
          <Box>{children}</Box>
        </CardContent>
      )}
    </Card>
  )
}

export default connect(mapStateToProps)(VerifyDomainPage)
